/**
 * Word-Level-Diff + Hilfen für das diff-basierte Ersetzen in Word (erhält granulare
 * Tracked Changes statt eines Ganz-Absatz-Replace). Reiner, testbarer Code — kein Office.js.
 */

export type DiffType = "EQUAL" | "ADDED" | "REMOVED";
export interface DiffPart {
  type: DiffType;
  value: string;
}

/** Word-Suche scheitert/ist instabil an diesen Zeichen → bei der Suche überspringen. */
const ANNOYING_CHARS = new Set([" ", "​", "‌", "‍", "﻿", "\t"]);
// Zero-width & BOM normalisieren; NBSP → normales Leerzeichen.
const ZERO_WIDTH_RE = /[​‌‍﻿]/g;

/** Normalisiert „lästige" Unicode-Zeichen, damit Vergleich/Suche robust sind. */
export function cleanUnicode(s: string): string {
  return s.replace(ZERO_WIDTH_RE, "").replace(/ /g, " ");
}

/** Tokenisiert in Wörter UND Whitespace-Läufe (verlustfrei: join() rekonstruiert den String). */
function tokenize(s: string): string[] {
  return s.match(/\s+|\S+/g) ?? [];
}

/**
 * Word-Level-Diff (LCS). Liefert zusammengefasste Läufe gleichen Typs in Reihenfolge,
 * sodass die Konkatenation der EQUAL+REMOVED den Originaltext, EQUAL+ADDED den Zieltext ergibt.
 */
export function diffWords(a: string, b: string): DiffPart[] {
  const x = tokenize(a);
  const y = tokenize(b);
  const n = x.length;
  const m = y.length;

  // LCS-DP
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = x[i] === y[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const raw: DiffPart[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (x[i] === y[j]) {
      raw.push({ type: "EQUAL", value: x[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      raw.push({ type: "REMOVED", value: x[i] });
      i++;
    } else {
      raw.push({ type: "ADDED", value: y[j] });
      j++;
    }
  }
  while (i < n) raw.push({ type: "REMOVED", value: x[i++] });
  while (j < m) raw.push({ type: "ADDED", value: y[j++] });

  // Läufe gleichen Typs zusammenfassen
  const out: DiffPart[] = [];
  for (const part of raw) {
    const last = out[out.length - 1];
    if (last && last.type === part.type) last.value += part.value;
    else out.push({ ...part });
  }
  return out;
}

/**
 * Eine Ersetz-Operation: ersetze die Spanne STRIKT zwischen `prevAnchor` und `nextAnchor`
 * (beides unveränderte Wörter; null = Absatzanfang/-ende) durch den Zieltext-Ausschnitt
 * [newStart, newEnd). Die Spanne enthält den umgebenden Trenn-Whitespace → kein Verkleben,
 * keine doppelten Leerzeichen. Eine Operation = ein zusammenhängender Tracked Change.
 */
export interface ReplaceOp {
  prevAnchor: string | null;
  nextAnchor: string | null;
  newStart: number;
  newEnd: number;
}

const hasWord = (s: string): boolean => /\S/.test(s);

/**
 * Plant das granulare Ersetzen aus einem Wort-Diff: gruppiert die Änderungen in Blöcke,
 * die von unveränderten WÖRTERN (Anker) begrenzt sind, und liefert pro Block eine ReplaceOp.
 * Whitespace-only-EQUAL-Teile werden in den Block absorbiert (sie sind keine Anker).
 */
export function planGranularReplace(oldText: string, target: string): ReplaceOp[] {
  const parts = diffWords(oldText, target);
  let oldPos = 0;
  let newPos = 0;
  const segs = parts.map((p) => {
    const seg = { type: p.type, value: p.value, oldStart: oldPos, newStart: newPos };
    if (p.type !== "ADDED") oldPos += p.value.length;
    if (p.type !== "REMOVED") newPos += p.value.length;
    return seg;
  });
  const isBoundary = (seg: (typeof segs)[number]): boolean => seg.type === "EQUAL" && hasWord(seg.value);

  const ops: ReplaceOp[] = [];
  let i = 0;
  while (i < segs.length) {
    if (isBoundary(segs[i])) {
      i++;
      continue;
    }
    const blockStart = i;
    while (i < segs.length && !isBoundary(segs[i])) i++;
    const blockEnd = i; // exklusiv
    if (!segs.slice(blockStart, blockEnd).some((s) => s.type !== "EQUAL")) continue; // nur ws-EQUAL → keine Änderung

    let prevAnchor: string | null = null;
    let newStart: number;
    if (blockStart > 0 && isBoundary(segs[blockStart - 1])) {
      const b = segs[blockStart - 1];
      prevAnchor = (b.value.match(/\S+(?=\s*$)/) ?? [null])[0];
      const wordEnd = b.value.replace(/\s+$/, "").length; // Index hinter dem letzten Wort
      newStart = b.newStart + wordEnd;
    } else {
      newStart = segs[blockStart].newStart;
    }

    let nextAnchor: string | null = null;
    let newEnd: number;
    if (blockEnd < segs.length && isBoundary(segs[blockEnd])) {
      const b = segs[blockEnd];
      nextAnchor = (b.value.match(/^\s*(\S+)/) ?? [null, null])[1];
      const leadingWs = b.value.length - b.value.replace(/^\s+/, "").length;
      newEnd = b.newStart + leadingWs; // Start des ersten Worts
    } else {
      newEnd = target.length;
    }

    ops.push({ prevAnchor, nextAnchor, newStart, newEnd });
  }
  return ops;
}

const WORD_SEARCH_LIMIT = 254; // Word range.search() Maximum

/**
 * Zerlegt einen Diff-Wert in suchbare Segmente: ≤ 254 Zeichen, an Whitespace-Grenzen
 * gesplittet, leere/whitespace-only/„lästige" Segmente entfernt (für die Word-Suche).
 */
export function searchSegments(value: string): string[] {
  const out: string[] = [];
  for (const piece of value.split(/(\s+)/)) {
    if (piece === "" || piece.trim() === "") continue; // Whitespace-Läufe nicht suchen
    if (ANNOYING_CHARS.has(piece)) continue;
    for (let k = 0; k < piece.length; k += WORD_SEARCH_LIMIT) {
      const seg = piece.slice(k, k + WORD_SEARCH_LIMIT);
      if (seg) out.push(seg);
    }
  }
  return out;
}
